/// useExecutionMetrics: React hook for aggregated execution metrics.
/// Reads from debugStore.metrics and computes derived values
/// (elapsed time, steps per second).
///
/// Phase 20, Plan 03.

import { useState, useEffect } from "react";
import { useDebugStore } from "@/stores/debugStore";
import type { ExecutionMetrics } from "@/types/debug";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook for reading execution metrics from the debug session.
 * Computes derived values that update every second while the session is active.
 *
 * @returns Metrics object, elapsed time in seconds, and steps per second.
 */
export function useExecutionMetrics() {
  const metrics: ExecutionMetrics = useDebugStore((s) => s.metrics);
  const mode = useDebugStore((s) => s.mode);

  const [elapsedTime, setElapsedTime] = useState(0);
  const [stepsPerSecond, setStepsPerSecond] = useState(0);

  // Update derived metrics on a 1-second interval while session is active
  useEffect(() => {
    function update() {
      const currentMetrics = useDebugStore.getState().metrics;
      if (currentMetrics.startTime) {
        const elapsedMs = Date.now() - currentMetrics.startTime;
        const elapsedSec = elapsedMs / 1000;
        setElapsedTime(elapsedSec);
        setStepsPerSecond(elapsedSec > 0 ? currentMetrics.completedSteps / elapsedSec : 0);
      } else {
        setElapsedTime(0);
        setStepsPerSecond(0);
      }
    }

    // Compute immediately on mount / metrics change
    update();

    // Only tick while session is active
    if (mode !== "idle") {
      const interval = setInterval(update, 1000);
      return () => clearInterval(interval);
    }
  }, [mode, metrics.startTime, metrics.completedSteps]);

  return { metrics, elapsedTime, stepsPerSecond };
}
